import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import {
    Modal,
    Form,
    Select,
    DatePicker,
    Button
} from '@gpstrackit/gtc-react-controls';

import { Alert } from '@mui/material';
import moment from 'moment';

import { NotificationManager } from 'Components/Notifications';

//actions
import {
    reset,
    closeCreateEditModal,
    getAllDriverStatus,
    createEditDriverStatusLog
} from './redux/slice';
import { DEFAULT_DRIVER_STATUS_ACTIONS } from '../../constants';
import {
    GetFormatForMoment
} from 'Core/data/Helpers';
import {
    toPickerDateByTimezone,
    fromPickerDateByTimezone
} from 'Modules/.common/utils/dateTimeByTimezone';

const DriverStatusLogCreateEdit = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const { refresh } = props;

    const formRef = Form.useForm();

    const open = useSelector(state => state.driverStatusLogCreateEditRedux.open);
    const response = useSelector(state => state.driverStatusLogCreateEditRedux.response);
    const loading = useSelector(state => state.driverStatusLogCreateEditRedux.loading);
    const driverStatusLog = useSelector(state => state.driverStatusLogCreateEditRedux.driverStatusLog);
    const driver = useSelector(state => state.driverStatusLogCreateEditRedux.driver);
    const lastLog = useSelector(state => state.driverStatusLogCreateEditRedux.lastLog);
    const driverStates = useSelector(state => state.driverStatusLogCreateEditRedux.driverStates);
    const error = useSelector(state => state.driverStatusLogCreateEditRedux.error);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);
    const timeZone = useSelector(state => state.securityRedux.user.timeZone);

    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (open) {
            dispatch(getAllDriverStatus({}));
        }
    }, [open]);

    useEffect(() => {
        if (open && formRef?.ref && driverStates?.length) {
            if (driverStatusLog) {
                let timer = setTimeout(() => {
                    let currentStatus = driverStates?.find(driverStatus => driverStatus?.id == driverStatusLog?.statusId)
                    formRef.setValues({ 
                        status: currentStatus ? { ...currentStatus, name: DEFAULT_DRIVER_STATUS_ACTIONS[currentStatus?.id] ? messages[DEFAULT_DRIVER_STATUS_ACTIONS[currentStatus?.id]] : currentStatus.name } : null,
                        statusLogOn: toPickerDateByTimezone(driverStatusLog?.statusLogOn, timeZone)
                    });
                    timer = null;
                }, 100);
            } else {
                let timer = setTimeout(() => {
                    formRef.setValues({ statusLogOn: toPickerDateByTimezone(moment().toISOString(), timeZone) });
                    timer = null;
                }, 100);
            }
        }
    }, [formRef?.ref, open, driverStatusLog, driverStates, timeZone]);

    useEffect(() => {
        if (open && response && !response?.error) {
            NotificationManager.success(
                messages['driverStatusLogCreateEdit_successMessage'],
                messages['driverStatusLogCreateEdit_success'],
                3000,
                null,
                null,
                'success',
                null
            );

            if (refresh) refresh();
            handleClose();
        }
    }, [open, response]);

    useEffect(() => {
        if (open && error) {
            let message = error?.error;

            if (message === "Invalid driver status log") {
                message = messages['driverStatusLogCreateEdit_invalidDriverStatus'];
                const errorDetail = error.errorData?.errors?.[0]?.errorInfo?.[0];
                
                if (errorDetail && errorDetail?.statusLogOn) {
                    const momentFormat = GetFormatForMoment(dateformat);
                    message += ' ' + messages['driverStatusLogCreateEdit_invalidDriverStatusDetail'].replace('_DATE_', moment(errorDetail.statusLogOn).format(momentFormat));
                }
            }

            setErrorMessage(message || messages['errorMessage']);
            dispatch(reset({ error: false, response: null }));
        }
    }, [error, open, response]);

    const handleClose = useCallback(() => {
        formRef?.resetValues();
        setErrorMessage("");
        dispatch(closeCreateEditModal());
        dispatch(reset());
    });

    let title = `${driverStatusLog?.id ? messages['driverStatusLogCreateEdit_editTitle'] : messages['driverStatusLogCreateEdit_createTitle']}: ${driver?.name}`;

    return (
        <Modal
            title={title}
            open={open}
            loading={loading}
            position="right"
            size="medium"
            handleClose={handleClose}
            footer={(
                <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                    <Button
                        disabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant='secondary'
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loading || !driverStatusLog}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formRef?.submit();
                        }}
                        variant='primary'
                    >
                        {messages['save']}
                    </Button>
                </div>
            )}
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={formRef.ref}
                    loading={loading}
                    onValidSubmit={(values) => {
                        setErrorMessage("");
                        const newData = {
                            id: driverStatusLog?.id || null,
                            driverId: driver?.driverId,
                            statusId: values?.status?.id,
                            statusLogOn: fromPickerDateByTimezone(values?.statusLogOn, timeZone),
                            latitude: 0,
                            longitude: 0,
                            address: ""
                        };

                        dispatch(createEditDriverStatusLog(newData));
                    }}
                    onChangeValues={() => setErrorMessage("")}
                >
                    <div>
                        <Form.Item
                            name="status"
                            label={messages['driverStatusLogCreateEdit_action']}
                            rules={[{ required: true, message: 'required_field' }]}
                        >
                            <Select
                                options={driverStates.map((driverStatus) => {
                                    return {
                                        ...driverStatus,
                                        name: DEFAULT_DRIVER_STATUS_ACTIONS[driverStatus?.id] ? messages[DEFAULT_DRIVER_STATUS_ACTIONS[driverStatus?.id]] : driverStatus.name
                                    }
                                })}
                                isMulti={false}
                                textField="name"
                                valueField="id"
                                style={{ width: '100%' }}
                                isSearchable
                            />
                        </Form.Item>

                        <Form.Item
                            name="statusLogOn"
                            label={messages['driverStatusLogCreateEdit_datetime']}
                            rules={[
                                { required: true, message: 'required_field' }
                            ]}
                        >
                            <DatePicker
                                showTimePicker
                                maxDate={moment().toISOString()}
                            />
                        </Form.Item>

                        {errorMessage &&
                            <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                        }
                    </div>
                </Form>
            </div>
        </Modal>
    );
}

export default DriverStatusLogCreateEdit;